"use client";

import { Brush, Clock, Scissors } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StrategicPillars() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const disciplines = [{
    numeral: "§ 01",
    icon: Scissors,
    title: "Credit Architecture",
    description: "Beyond simple repair, we engineer personal credit profiles for maximum leverage. We restructure debt-to-income ratios and optimize utilization to transform your score into a powerful instrument for capital acquisition.",
    toolsLabel: "STRATEGY",
    tools: "Profile Audit · Debt Restructuring · Limit Optimization"
  }, {
    numeral: "§ 02",
    icon: Brush,
    title: "Business Funding",
    description: "Strategic access to high-limit credit lines and term loans without personal liability. We navigate the complex requirements of tier-one lenders to secure the liquidity your enterprise requires for aggressive growth.",
    toolsLabel: "STRATEGY",
    tools: "Tier-1 Lending · No-Doc Funding · Corporate Credit"
  }, {
    numeral: "§ 03",
    icon: Clock,
    title: "Wealth Strategy",
    description: "The preservation and multiplication of capital through elite financial coaching. We design bespoke pathways that bridge the gap between immediate funding needs and long-term generational wealth building.",
    toolsLabel: "STRATEGY",
    tools: "Asset Protection · Cash Flow Design · Mentorship"
  }];
  return <section data-section-id="3015" id="strategic-pillars" className="bg-background text-foreground relative overflow-hidden" style={{
    paddingTop: "180px",
    paddingBottom: "180px"
  }}>
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)",
      backgroundSize: "50px 50px"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        {}
        <div ref={headerRef} className={`text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono uppercase mb-10" style={{
          fontSize: "12px",
          letterSpacing: "0.2em",
          color: "var(--primary)"
        }}>
            — Strategic Pillars
          </p>
          <h2 className="font-default font-medium leading-[1.1] text-foreground mx-auto max-w-4xl" style={{
          fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
          tracking: "-0.01em"
        }}>
            Elite solutions for <span className="text-primary">serious</span>
            <br />
            capital requirements.
          </h2>
        </div>

        {}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3" style={{
        columnGap: "64px",
        rowGap: "80px"
      }}>
          {disciplines.map((discipline, i) => {
          const Icon = discipline.icon;
          return <DisciplineCard key={i} data-index={i} index={i} numeral={discipline.numeral} Icon={Icon} title={discipline.title} description={discipline.description} toolsLabel={discipline.toolsLabel} tools={discipline.tools} />;
        })}
        </div>
      </div>
    </section>;
}
function DisciplineCard({
  index,
  numeral,
  Icon,
  title,
  description,
  toolsLabel,
  tools,
  ...rest
}: {
  index: number;
  numeral: string;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  toolsLabel: string;
  tools: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`group relative flex flex-col text-center transition-all ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDuration: "900ms",
    transitionDelay: `${index * 150}ms`
  }}>
      {}
      <div className="flex justify-center mb-8">
        <Icon className="text-foreground/40 group-hover:text-primary transition-colors duration-500" strokeWidth={1} style={{
        width: "64px",
        height: "64px"
      }} />
      </div>

      {}
      <p className="font-mono font-bold mb-6" style={{
      fontSize: "12px",
      color: "var(--primary)",
      letterSpacing: "0.2em"
    }}>
        {numeral}
      </p>

      {}
      <h3 className="font-default font-semibold text-foreground mb-6 uppercase tracking-wider" style={{
      fontSize: "1.25rem",
      lineHeight: "1.4"
    }}>
        {title}
      </h3>

      {}
      <p className="font-default text-foreground/70 mx-auto max-w-xs" style={{
      fontSize: "16px",
      lineHeight: "1.6"
    }}>
        {description}
      </p>

      {}
      <div className="mt-10 mb-5 mx-auto w-16 h-px bg-primary/30" />

      {}
      <p className="font-mono uppercase text-foreground/60 mx-auto" style={{
      fontSize: "10px",
      letterSpacing: "0.2em"
    }}>
        <span className="text-primary font-bold">{toolsLabel}</span>
        <span className="mx-2">·</span>
        {tools}
      </p>

      {}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full" />
    </div>;
}